import { formatTicker } from '@api';
import { generatePath } from '@helpers';
import { AssetLogoContainer } from '@terminal/components/asset-logo-container/AssetLogoContainer';
import { SectionTitle } from '@terminal/components/section-title/SectionTitle';
import { getKnowledgeGraphSearchParams } from '@terminal/modules/overview-knowledge-graph/utils/knowledge-graph-utils';
import { useEntities } from '@terminal/react-query-hooks/entities/useEntities';
import { useCompanyCompetitorWithThemes } from '@terminal/react-query-hooks/knowledge-graph/useKnowledgeGraph';
import { appPaths } from '@terminal/routes/app-paths';
import { queryKeys } from '@terminal/routes/query-params';
import { useCompetitorsDrawer } from '@terminal/widgets/knowledge-graph-widget/components/competitors-tab/competitors-drawer/useCompetitorsDrawer';
import { TopRankIcon } from '@terminal/widgets/knowledge-graph-widget/components/top-rank-icon/TopRankIcon';
import { KnowledgeGraphTabType } from '@terminal/widgets/knowledge-graph-widget/KnowledgeGraphWidget';
import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  matchPath,
  useLocation,
  useNavigate,
  useSearchParams,
} from 'react-router';

import { LoadingCell } from '../shared/loading-cell/LoadingCell';
import styles from './CompetitorsKnowledgeGraph.module.scss';

export interface CompetitorsKnowledgeGraphProps {
  entityTag: string;
  isInDrawer?: boolean;
}

export const CompetitorsKnowledgeGraph = ({
  entityTag,
  isInDrawer = true,
}: CompetitorsKnowledgeGraphProps) => {
  const { t } = useTranslation(['widget']);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { openDrawer } = useCompetitorsDrawer();

  const {
    data: competitorsData = [],
    isFetching,
    isError,
  } = useCompanyCompetitorWithThemes({
    entityTag,
    limit: 3,
  });

  const entityTags = competitorsData.map(item => item.company);

  const { data: entitiesData = [], isLoading: isEntitiesLoading } = useEntities(
    {
      entityTags,
      enabled: !!entityTags.length,
    }
  );

  if (isError) {
    throw new Error('Failed to load CompetitorsKnowledgeGraph');
  }

  // Create a map of entity tag to competitor data (for rank lookup)
  const competitorDataByTag = new Map(
    competitorsData.map(item => [item.company, item])
  );

  const isAssetOverviewPage = matchPath(
    { path: appPaths.assetOverview, end: false, caseSensitive: false },
    location.pathname
  );

  const getTooltip = (context: string) => {
    if (isAssetOverviewPage) {
      return undefined;
    }

    /** i18next-extract-mark-context-next-line ["competitors"] */
    return t('widget:assetOverview:knowledgeGraphTooltipRevamp', {
      context: context,
    });
  };

  const knowledgeGraphSearchParams = getKnowledgeGraphSearchParams({
    entityTag,
    searchParams,
    subtab: KnowledgeGraphTabType.Competitors,
  }).toString();
  const linkToKnowledgeGraph = `${appPaths.assetOverview}?${knowledgeGraphSearchParams}`;

  const onClickHandler = isInDrawer
    ? (competitorEntityTag: string) => {
        const requestPath = generatePath({
          basePath: appPaths.assetOverview,
          params: {
            [queryKeys.assetOverviewAsset]: competitorEntityTag,
          },
        });
        navigate(requestPath);
      }
    : openDrawer;

  return (
    <div>
      <SectionTitle
        title={t('widget:assetOverview.knowledgeGraph.subtab.competitors')}
        variant="small"
        linkTo={linkToKnowledgeGraph}
        tooltip={getTooltip('competitors')}
      />
      {isFetching || isEntitiesLoading ? (
        <div>
          {Array.from({ length: 3 }).map((_, idx) => (
            <div className={styles.competitorRow} data-competitor-row key={idx}>
              <LoadingCell />
            </div>
          ))}
        </div>
      ) : (
        <div>
          {entitiesData.map(entity => {
            const competitorData = competitorDataByTag.get(entity.tag);
            const rank = competitorData?.relationship.rank;

            if (!rank) {
              return null;
            }

            return (
              <div
                className={styles.competitorRow}
                data-competitor-row
                onClick={() => onClickHandler(entity.tag)}
                key={entity.tag}
              >
                <div className={styles.competitorNameWrapper}>
                  <AssetLogoContainer entity={entity} size={32} />
                  <div className={styles.flexColumn}>
                    <div className={styles.titleLabel}>
                      {entity.name_short || entity.name}
                    </div>
                    <span className={styles.competitorName}>
                      {formatTicker(entity)}
                    </span>
                  </div>
                </div>
                <TopRankIcon rank={rank} iconSize={28} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
